import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  checkApiHealth,
  fetchAdminContent,
  getAdminToken,
  isAdminSessionError,
  saveAdminContent,
  setAdminToken,
} from '../content/contentApi';
import { hydrateAllContent } from '../content/hydrateContent';
import { STATIC_SITE_DEFAULTS } from '../content/staticDefaults';
import { useContent } from '../content/ContentProvider';
import { HEADER_LOGO_URL } from '../config/cloudinaryBrandImages';
import { mergeWithDefaults, resolveSiteContent } from './mergeContent';
import CaseStudiesEditor from './editors/CaseStudiesEditor';
import BlogsEditor from './editors/BlogsEditor';
import TeamEditor from './editors/TeamEditor';
import TestimonialsEditor from './editors/TestimonialsEditor';
import MediaEditor from './editors/MediaEditor';
import ServicePagesEditor from './editors/ServicePagesEditor';
import AboutPageTab from './editors/AboutPageTab';
import HomePageTab from './editors/HomePageTab';
import ServicesPageTab from './editors/ServicesPageTab';
import CaseStudiesPageTab from './editors/CaseStudiesPageTab';
import ContactPageTab from './editors/ContactPageTab';
import ConfirmDialog from './components/ConfirmDialog';
import { AdminPersistProvider } from './AdminPersistContext';
import { useAdminContentPersist } from './hooks/useAdminContentPersist';
import './Admin.css';

const TABS = [
  { id: 'caseStudies', label: 'Case studies' },
  { id: 'blogs', label: 'Blog' },
  { id: 'team', label: 'Team' },
  { id: 'testimonials', label: 'Testimonials' },
  { id: 'media', label: 'Media URLs' },
  { id: 'servicePages', label: 'Service Pages' },
  { id: 'aboutPage', label: 'About Page' },
  { id: 'homePage', label: 'Home Page' },
  { id: 'servicesPage', label: 'Services Page' },
  { id: 'caseStudiesPage', label: 'Case Studies Page' },
  { id: 'contactPage', label: 'Contact Page' },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const tabListId = useId();
  const { refresh, updatedAt, fromApi } = useContent();
  const [tab, setTab] = useState('servicePages');
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [apiOnline, setApiOnline] = useState(null);
  const [defaultsConfirmOpen, setDefaultsConfirmOpen] = useState(false);

  const handleSessionExpired = useCallback(
    (err) => {
      const msg = err?.message || 'Session expired. Please sign in again.';
      setError(msg);
      setAdminToken(null);
      navigate('/admin/login?reason=session', { replace: true });
    },
    [navigate]
  );

  const persist = useAdminContentPersist({
    refresh,
    onError: (msg, err) => {
      if (isAdminSessionError(err)) {
        handleSessionExpired(err);
        return;
      }
      setError(msg);
    },
    onSuccess: setMessage,
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const online = await checkApiHealth();
    setApiOnline(online);
    try {
      if (!online) throw new Error('Content API is offline. Run: npm run server (databiqs-website-backend)');
      const api = await fetchAdminContent();
      const merged = mergeWithDefaults(api);
      setContent(merged);
    } catch (e) {
      if (isAdminSessionError(e)) {
        handleSessionExpired(e);
        return;
      }
      setError(e.message);
      setContent(STATIC_SITE_DEFAULTS);
    } finally {
      setLoading(false);
    }
  }, [handleSessionExpired]);

  useEffect(() => {
    if (!getAdminToken()) {
      navigate('/admin/login', { replace: true });
      return;
    }
    load();
  }, [load, navigate]);

  const { setBaseline, onContentChange, persistNow, saving: persistSaving, pending } = persist;
  const prevLoadingRef = useRef(true);
  useEffect(() => {
    if (prevLoadingRef.current && !loading && content) {
      setBaseline(content);
    }
    prevLoadingRef.current = loading;
  }, [loading, content, setBaseline]);

  const publishFullContent = async (payload, successMsg) => {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const online = await checkApiHealth();
      setApiOnline(online);
      if (!online) throw new Error('Content API is offline. Run: npm run server (databiqs-website-backend)');
      const apiBody = {
        services: payload.services,
        caseStudies: payload.caseStudies,
        blogs: payload.blogs,
        team: payload.team,
        testimonials: payload.testimonials,
        media: payload.media,
        servicePages: payload.servicePages,
        aboutPage: payload.aboutPage,
        homePage: payload.homePage,
        servicesPage: payload.servicesPage,
        caseStudiesPage: payload.caseStudiesPage,
        contactPage: payload.contactPage,
        updatedAt: new Date().toISOString(),
      };
      await saveAdminContent(apiBody);
      const resolved = resolveSiteContent(apiBody);
      try {
        hydrateAllContent(resolved);
      } catch (hydrateErr) {
        console.error('[admin] hydrate after save failed:', hydrateErr);
      }
      await refresh(apiBody.updatedAt);
      setContent(resolved);
      setBaseline(resolved);
      setMessage(successMsg);
    } catch (e) {
      if (isAdminSessionError(e)) {
        handleSessionExpired(e);
        return;
      }
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleContentChange = (next) => {
    onContentChange(next, setContent);
  };

  const handleSaveNow = async () => {
    if (!content) return;
    setMessage('');
    setError('');
    try {
      await persistNow();
    } catch (e) {
      if (isAdminSessionError(e)) handleSessionExpired(e);
    }
  };

  const handlePublishDefaults = () => {
    setDefaultsConfirmOpen(true);
  };

  const confirmPublishDefaults = () => {
    setDefaultsConfirmOpen(false);
    publishFullContent(STATIC_SITE_DEFAULTS, 'Full site content loaded and saved to the server.');
  };

  const isSaving = saving || persistSaving;
  const saveStatusLabel = isSaving
    ? 'Saving to server…'
    : pending
      ? 'Unsaved changes…'
      : 'Save to server';

  const logout = () => {
    setAdminToken(null);
    navigate('/admin/login');
  };

  const handleTabKeyDown = (e) => {
    const idx = TABS.findIndex((t) => t.id === tab);
    if (idx < 0) return;
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      setTab(TABS[(idx + 1) % TABS.length].id);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      setTab(TABS[(idx - 1 + TABS.length) % TABS.length].id);
    } else if (e.key === 'Home') {
      e.preventDefault();
      setTab(TABS[0].id);
    } else if (e.key === 'End') {
      e.preventDefault();
      setTab(TABS[TABS.length - 1].id);
    }
  };

  if (loading || !content) {
    return (
      <div className="admin-root">
        <div className="admin-loading">
          <img src={HEADER_LOGO_URL} alt="Databiqs" className="admin-loading__logo" />
          <p>Loading admin panel…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-root admin-app">
      <header className="admin-topbar">
        <div className="admin-topbar__inner">
          <div className="admin-brand">
            <Link to="/" className="admin-brand__logo-link" target="_blank" rel="noopener noreferrer" title="View website">
              <img src={HEADER_LOGO_URL} alt="Databiqs" className="admin-brand__logo" width={140} height={40} />
            </Link>
            <span className="admin-brand__divider" aria-hidden>
              |
            </span>
            <h1 className="admin-brand__title">Admin Panel</h1>
          </div>

          <div className="admin-topbar__actions">
            <button
              type="button"
              className="admin-btn admin-btn--primary"
              onClick={handleSaveNow}
              disabled={isSaving || !apiOnline}
            >
              {saveStatusLabel}
            </button>
            <div className="admin-topbar__secondary">
              <Link to="/" className="admin-btn admin-btn--ghost admin-btn--sm" target="_blank" rel="noopener noreferrer">
                View site
              </Link>
              <button type="button" className="admin-btn admin-btn--ghost admin-btn--sm" onClick={load} disabled={isSaving}>
                Reload from server
              </button>
              <button type="button" className="admin-btn admin-btn--ghost admin-btn--sm" onClick={logout}>
                Log out
              </button>
            </div>
          </div>
        </div>
      </header>

      {(message || error) && (
        <div className="admin-alerts">
          {message ? <p className="admin-success">{message}</p> : null}
          {error ? <p className="admin-error">{error}</p> : null}
        </div>
      )}

      <div
        className={`admin-status-strip ${apiOnline ? 'admin-status-strip--ok' : 'admin-status-strip--warn'}`}
        role="status"
      >
        <span className="admin-status-strip__dot" aria-hidden />
        {apiOnline ? (
          <span className="admin-status-strip__text">
            {isSaving
              ? 'Saving to server…'
              : pending
                ? 'Editing — auto-saves shortly'
                : fromApi && updatedAt
                  ? `Saved on server · ${new Date(updatedAt).toLocaleString()}`
                  : 'Ready · edits auto-save to server'}
          </span>
        ) : (
          <span className="admin-status-strip__text">Backend offline — run npm run server</span>
        )}
        {!fromApi && apiOnline ? (
          <button
            type="button"
            className="admin-btn admin-btn--ghost admin-btn--sm"
            onClick={handlePublishDefaults}
            disabled={isSaving}
          >
            Load site into CMS
          </button>
        ) : null}
      </div>

      <AdminPersistProvider
        value={{
          persistNow,
          saving: isSaving,
          pending,
        }}
      >
      <div className="admin-layout">
        <div className="admin-shell">
          <div className="admin-tabs-card">
            <div className="admin-tabs-scroll">
              <div
                id={tabListId}
                className="admin-tablist"
                role="tablist"
                aria-label="Content sections"
                onKeyDown={handleTabKeyDown}
              >
                {TABS.map((t) => {
                  const selected = tab === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      role="tab"
                      id={`tab-${t.id}`}
                      aria-selected={selected}
                      aria-controls={`panel-${t.id}`}
                      tabIndex={selected ? 0 : -1}
                      className={`admin-tab${selected ? ' admin-tab--active' : ''}`}
                      onClick={() => setTab(t.id)}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="admin-tabpanels">
              {TABS.map((t) => (
                <div
                  key={t.id}
                  id={`panel-${t.id}`}
                  role="tabpanel"
                  aria-labelledby={`tab-${t.id}`}
                  hidden={tab !== t.id}
                  className="admin-tabpanel"
                >
                  {tab === t.id && t.id === 'caseStudies' && (
                    <CaseStudiesEditor content={content} onChange={handleContentChange} />
                  )}
                  {tab === t.id && t.id === 'blogs' && (
                    <BlogsEditor content={content} onChange={handleContentChange} />
                  )}
                  {tab === t.id && t.id === 'team' && (
                    <TeamEditor content={content} onChange={handleContentChange} />
                  )}
                  {tab === t.id && t.id === 'testimonials' && (
                    <TestimonialsEditor content={content} onChange={handleContentChange} />
                  )}
                  {tab === t.id && t.id === 'media' && (
                    <MediaEditor content={content} onChange={handleContentChange} />
                  )}
                  {tab === t.id && t.id === 'servicePages' && (
                    <ServicePagesEditor content={content} onChange={handleContentChange} />
                  )}
                  {tab === t.id && t.id === 'aboutPage' && <AboutPageTab />}
                  {tab === t.id && t.id === 'homePage' && <HomePageTab />}
                  {tab === t.id && t.id === 'servicesPage' && <ServicesPageTab />}
                  {tab === t.id && t.id === 'caseStudiesPage' && <CaseStudiesPageTab />}
                  {tab === t.id && t.id === 'contactPage' && <ContactPageTab />}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      </AdminPersistProvider>

      <ConfirmDialog
        open={defaultsConfirmOpen}
        title="Load site into CMS"
        message="Copy the full current website content into the CMS? This overwrites saved data in the admin."
        confirmLabel="Load & publish"
        cancelLabel="Cancel"
        danger
        onConfirm={confirmPublishDefaults}
        onCancel={() => setDefaultsConfirmOpen(false)}
      />
    </div>
  );
}
